// src/pages/Contact.jsx
import React from 'react';
import PageTitle from '../components/SEO/PageTitle';
import { Link } from 'react-router-dom';

const Contact = () => {
    return (
        <div className="container mx-auto px-4 py-12 max-w-4xl">
            <PageTitle 
    title="Contact Us" 
    description="Get in touch with the GradPath team. We're here to help with your career journey."
/>
            {/* Header */}
            <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-2xl mb-4">
                    <i className="fas fa-envelope text-white text-3xl"></i>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gradpath-dark mb-3">
                    Contact Us
                </h1>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                    Get in touch with the GradPath team
                </p>
            </div>

            {/* Contact Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1">
                    <div className="w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-building text-white text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gradpath-dark mb-2">For Recruiters</h3>
                    <p className="text-gray-600 text-sm mb-3">
                        Interested in posting jobs or partnering with us?
                    </p>
                    <a 
                        href="mailto:recruiters@gradpath.co.za" 
                        className="text-gradpath-green hover:underline font-medium"
                    >
                        recruiters@gradpath.co.za
                    </a>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1">
                    <div className="w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-graduation-cap text-white text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gradpath-dark mb-2">For Applicants</h3>
                    <p className="text-gray-600 text-sm mb-3">
                        Questions about job applications or your account?
                    </p>
                    <a 
                        href="mailto:applicants@gradpath.co.za" 
                        className="text-gradpath-green hover:underline font-medium"
                    >
                        applicants@gradpath.co.za
                    </a>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1">
                    <div className="w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-headset text-white text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gradpath-dark mb-2">Technical Support</h3>
                    <p className="text-gray-600 text-sm mb-3">
                        Having technical issues with the platform?
                    </p>
                    <a 
                        href="mailto:support@gradpath.co.za" 
                        className="text-gradpath-green hover:underline font-medium"
                    >
                        support@gradpath.co.za
                    </a>
                </div>
            </div>

            {/* Additional Info */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-8">
                <div className="grid md:grid-cols-2 gap-8">
                    {/* Left Column */}
                    <div>
                        <h2 className="text-xl font-bold text-gradpath-dark mb-4 flex items-center gap-2">
                            <i className="fas fa-clock text-gradpath-green"></i>
                            Response Times
                        </h2>
                        <p className="text-gray-700 mb-4">
                            We aim to respond to all inquiries within <strong>2-3 business days</strong>. For urgent matters, 
                            please mark your email as <strong>"URGENT"</strong> in the subject line.
                        </p>

                        <h2 className="text-xl font-bold text-gradpath-dark mb-4 flex items-center gap-2 mt-6">
                            <i className="fas fa-map-marker-alt text-gradpath-green"></i>
                            Office Location
                        </h2>
                        <p className="text-gray-700">
                            GradPath operates remotely across South Africa. While we don't have a physical office, 
                            we conduct virtual meetings and are available via email and phone.
                        </p>
                    </div>

                    {/* Right Column */}
                    <div>
                        <h2 className="text-xl font-bold text-gradpath-dark mb-4 flex items-center gap-2">
                            <i className="fas fa-share-alt text-gradpath-green"></i>
                            Follow Us
                        </h2>
                        <div className="flex gap-4 mb-6">
                            <a 
                                href="https://www.linkedin.com/company/gradpath-south-africa/" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-gray-600 hover:text-gradpath-green text-4xl transition-colors"
                                aria-label="LinkedIn"
                            >
                                <i className="fab fa-linkedin"></i>
                            </a>
                            <a 
                                href="https://www.instagram.com/gradpathza/" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-gray-600 hover:text-gradpath-green text-4xl transition-colors"
                                aria-label="Instagram"
                            >
                                <i className="fab fa-instagram"></i>
                            </a>
                            <a 
                                href="#" 
                                className="text-gray-600 hover:text-gradpath-green text-4xl transition-colors"
                                onClick={(e) => e.preventDefault()}
                                aria-label="Twitter"
                            >
                                <i className="fab fa-twitter"></i>
                            </a>
                            <a 
                                href="#" 
                                className="text-gray-600 hover:text-gradpath-green text-4xl transition-colors"
                                onClick={(e) => e.preventDefault()}
                                aria-label="Facebook"
                            >
                                <i className="fab fa-facebook"></i>
                            </a>
                        </div>

                        <h2 className="text-xl font-bold text-gradpath-dark mb-4 flex items-center gap-2">
                            <i className="fas fa-envelope text-gradpath-green"></i>
                            General Inquiries
                        </h2>
                        <p className="text-gray-700">
                            For general questions, feedback, or partnership opportunities:
                        </p>
                        <a 
                            href="mailto:info@gradpath.co.za" 
                            className="text-gradpath-green hover:underline font-medium text-lg block mt-2"
                        >
                            info@gradpath.co.za
                        </a>
                    </div>
                </div>

                {/* Security Notice */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                        <div className="flex items-start gap-3">
                            <i className="fas fa-shield-alt text-yellow-600 text-xl mt-0.5"></i>
                            <div>
                                <p className="text-sm text-yellow-800 font-medium">Security Notice</p>
                                <p className="text-sm text-yellow-700">
                                    Never share your password or sensitive personal information via email. 
                                    GradPath will never ask for your password.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        

            {/* Back to Home Link */}
            <div className="text-center">
                <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 text-gradpath-green hover:underline"
                >
                    <i className="fas fa-arrow-left"></i>
                    Back to Home
                </Link>
            </div>
        </div>
    );
};

export default Contact;